<template>
    <div class="container-fluid edit-container">
        <div class="row justify-content-center mt-5">
            <div class="col-12 case-edit-col">
                <!-- Node Settings Header -->
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex align-items-center">
                            <div class="flex-grow-1">
                                <h2 class="h5 mb-0 font-weight-bolder settings-header">Screen Settings</h2>
                            </div>
                            <div class="flex-grow-0 flex-shrink-0">
                                <div class="d-flex align-items-center">
                                    <div class="mr-4 d-none d-lg-block">
                                        <router-link :to="`${caseBaseUrl}map?nodeID=${nodeID}&scrollToNavItem=true`">
                                            <span
                                                class="icon icon-glyphicons-basic-cluster icon-mr align-middle"
                                            ></span>
                                            <span class="align-middle">View in Map</span>
                                        </router-link>
                                    </div>
                                    <div>
                                        <a
                                            @click="previewNode()"
                                            role="button"
                                            :class="{ disabled: loading }"
                                            class="btn btn-sm btn-primary"
                                        >
                                            <span
                                                class="icon icon-100 icon-glyphicons-basic-video-play icon-mr align-middle"
                                            ></span>
                                            <span class="align-middle">Preview & Edit</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <hr class="mt-3" />
                <br />

                <!-- Node Settings -->
                <div class="row">
                    <div class="col-12">
                        <div class="row form-group pb-1">
                            <label class="col-12 col-sm-4 col-form-label">
                                <span v-if="autoNumberingEnabled === false">Number & Label</span>
                                <span v-else>Screen Label</span>
                            </label>
                            <div class="col-12 col-sm-8">
                                <div class="d-flex">
                                    <div class="flex-grow-0 mr-2" v-if="!autoNumberingEnabled">
                                        <input
                                            v-model="node.number"
                                            placeholder="#"
                                            class="form-control number-input"
                                            type="number"
                                            min="0"
                                            dir="auto"
                                        />
                                    </div>
                                    <div class="flex-grow-1">
                                        <input v-model="node.title" class="form-control" type="text" maxlength="150" dir="auto" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <base-form
                            v-model="node"
                            :fields="nodeFormFieldsConfig"
                            :indent-columns="4"
                            v-on:edit="nodeFormEditCb($event)"
                            ref="nodeEditForm"
                        ></base-form>
                        <div class="row mb-3 pb-1">
                            <label for="continue-to-node" class="col-12 col-sm-4 col-form-label">
                                Continue to Screen
                            </label>
                            <div class="col-12 col-sm-8">
                                <node-select
                                    id="continue-to-node"
                                    v-model="node.continue_node"
                                    :node-options="nodeOptions"
                                    placeholder="Select a screen to continue to"
                                    ref="continueNodeSelect"
                                ></node-select>
                                <a
                                    class="badge badge-gray badge-small mr-2 align-middle"
                                    @click="openConditionalBranching()"
                                    >+ Conditional Branch</a
                                >
                                <small class="text-secondary align-middle" v-show="node.conditional_branches.length">
                                    {{ node.conditional_branches.length }} Condition<span
                                        v-show="node.conditional_branches.length > 1"
                                        >s</span
                                    >
                                    Added
                                </small>
                            </div>
                        </div>
                        <div class="row mb-3 pb-1">
                            <label for="continue-to-node" class="col-12 col-sm-4 col-form-label">
                                Continue Requirement
                            </label>
                            <div class="col-12 col-sm-8">
                                <div class="d-flex">
                                    <div class="flex-grow-1">
                                        <select
                                            v-model="node.correct_answers_required"
                                            :disabled="!node.continue_button"
                                            class="form-control"
                                        >
                                            <option
                                                v-for="option in continueRequirementOptions"
                                                :key="option.value"
                                                :value="option.value"
                                            >
                                                {{ option.label }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="flex-grow-0 ml-2" v-show="node.correct_answers_required === 'custom'">
                                        <input
                                            v-model="node.correct_answers_required_num"
                                            :disabled="!node.continue_button"
                                            class="form-control number-input"
                                            type="number"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-12">
                        <button
                            @click="saveNode()"
                            :disabled="editLocked"
                            type="button"
                            class="btn btn-sm btn-primary float-right"
                        >
                            Save
                        </button>
                    </div>
                </div>
                <br />
                <br />
                <br />

                <!-- Content Header -->
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex">
                            <div class="flex-grow-1 align-self-center">
                                <h2 class="h5 mb-0 font-weight-bolder settings-header">Content Preview</h2>
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="mt-3 mb-0" />
                <br />

                <!-- Content Preview -->
                <div class="row">
                    <div class="col-12">
                        <div class="px-2 pt-5 pb-3 rounded bg-white border border-gray">
                            <div
                                class="d-flex flex-wrap justify-content-center position-relative mx-3 mx-xxl-5 node-preview-container"
                            >
                                <node-content-preview
                                    :node="node"
                                    v-on:edit="openEditPanel($event)"
                                ></node-content-preview>
                            </div>
                            <div class="d-flex justify-content-end position-relative mt-1 edit-link">
                                <div>
                                    <a
                                        @click="previewNode()"
                                        role="button"
                                        :class="{ disabled: loading }"
                                        class="btn btn-sm btn-link"
                                    >
                                        <span
                                            class="icon icon-100 icon-glyphicons-basic-video-play icon-mr align-middle"
                                        ></span>
                                        <span class="align-middle">Edit Screen</span>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <br />
                <br />
                <br />

                <div class="row" v-show="node.previous_nodes.length">
                    <div class="col-12 col-md-4">
                        <p class="mb-2">
                            Previous Screens
                            <help-text
                                content="A list of previous screens that link to this one."
                                class="ml-1"
                            ></help-text>
                        </p>
                    </div>
                    <div class="col-12 col-md-8">
                        <span v-for="previousNode in node.previous_nodes" :key="previousNode.id">
                            <router-link class="badge badge-gray mr-2" :to="`${caseBaseUrl}nodes/${previousNode.id}/`"
                                >{{ previousNode.number }}. {{ previousNode.title }}</router-link
                            >
                        </span>
                    </div>
                </div>
                <div class="row" v-show="node.next_nodes.length">
                    <div class="col-12 col-md-4">
                        <p class="mb-2">
                            Next Screens
                            <help-text content="A list of screens that this screen leads to." class="ml-1"></help-text>
                        </p>
                    </div>
                    <div class="col-12 col-md-8">
                        <span v-for="nextNode in node.next_nodes" :key="nextNode.id">
                            <router-link class="badge badge-gray mr-2" :to="`${caseBaseUrl}nodes/${nextNode.id}/`"
                                >{{ nextNode.number }}. {{ nextNode.title }}</router-link
                            >
                        </span>
                    </div>
                </div>

                <br />
                <br />
                <br />

                <!-- Bottom Controls -->
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-end">
                            <div class="flex-grow-1">
                                <button @click="openComments()" class="btn btn-light btn-sm">
                                    <span class="icon icon-glyphicons-basic-chat-conversation icon-mr"></span>
                                    <span>Comments</span>
                                </button>
                            </div>
                            <div class="flex-grow-0">
                                <button @click="openCloneNode()" type="button" class="btn btn-light btn-sm mr-1">
                                    <span class="icon icon-glyphicons-basic-squares mr-1"></span>
                                    <span>Clone</span>
                                </button>
                                <button
                                    @click="openDeleteNode()"
                                    :disabled="editLocked"
                                    type="button"
                                    class="btn btn-light btn-sm"
                                >
                                    <span class="icon icon-glyphicons-basic-bin mr-1"></span>
                                    <span>Delete</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Node Modal -->
        <base-modal id="delete-node-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Delete Screen </template>
            <template>
                <p>
                    Delete screen
                    <strong>{{ node.title }}</strong>
                    ?
                </p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="deleteNode()" :disabled="modalLoading" class="btn btn-danger" type="button">
                    Delete
                </button>
            </template>
        </base-modal>

        <!-- Clone Node Modal -->
        <base-modal id="clone-node-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Clone Screen </template>
            <template>
                <p class="mb-2 pb-1">
                    Clone screen
                    <strong>{{ node.title }}</strong>
                    into:
                </p>
                <div class="mb-2">
                    <div class="custom-control custom-radio mb-2">
                        <input
                            v-model="cloneNodeOutsideCase"
                            :value="false"
                            type="radio"
                            id="clone-screen-internal"
                            class="custom-control-input"
                            :disabled="editLocked"
                        />
                        <label class="custom-control-label" for="clone-screen-internal">This learning experience</label>
                    </div>
                    <div class="custom-control custom-radio">
                        <input
                            v-model="cloneNodeOutsideCase"
                            :value="true"
                            type="radio"
                            id="clone-screen-external"
                            class="custom-control-input"
                        />
                        <label class="custom-control-label" for="clone-screen-external">A different learning experience</label>
                    </div>
                </div>
                <div v-show="cloneNodeOutsideCase" class="py-1 pl-3">
                    <library-item-selector
                        v-model="cloneIntoLibraryItem"
                        :item-types="['owned_case']"
                        placeholder="Select a learning experience to clone into..."
                        emptyText="No learning experiences found matching your search."
                        ref="itemSelector"
                    ></library-item-selector>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="cloneNode()" :disabled="cloneBtnDisabled" class="btn btn-primary" type="button">
                    Clone
                </button>
            </template>
        </base-modal>

        <!-- Create Node Modal -->
        <base-modal id="create-node-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> New Screen </template>
            <template>
                <base-form
                    v-model="newNode"
                    :fields="newNodeFieldsConfig"
                    :inline-style="true"
                    ref="createNodeForm"
                ></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="createNode()"
                    :disabled="editLocked || modalLoading"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Edit Continue Button Modal -->
        <base-modal id="edit-continue-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Edit Continue Button Label </template>
            <template>
                <base-form v-model="node" :fields="continueButtonFields" :inline-style="true" class="mt-3"></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="saveContinueButton()"
                    :disabled="editLocked || modalLoading"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- Comments Modal -->
        <base-modal id="comments-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="align-middle icon icon-glyphicons-basic-chat-conversation mr-2"></span>
                <span class="align-middle">Comments</span>    
            </template>
            <template>
                <node-comments
                    :comments="comments"
                    :edit-mode="false"
                    :show-admin-tools="true"
                    :show-link-details="true"
                    class="mt-2"
                ></node-comments>
                <div v-show="comments.length === 0 && !modalLoading" class="text-secondary text-center my-3">
                    No comments have been left on this screen yet.
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-light" type="button" data-dismiss="modal">Done</button>
            </template>
        </base-modal>

        <!-- Conditional Branching Editor -->
        <conditional-branch-editor
            ref="conditionalBranchEditor"
            :case-id="caseID"
            :node="node"
            :node-options="nodeOptions"
            :conditional-branches.sync="node.conditional_branches"
            :selectable-choices="panelSelectableChoices"
            :categories-choices="panelCategoriesChoices"
            :choices-with-input="panelFormChoices"
            :panels="nodePanelsFlat"
            v-on:updated="conditionalBranchUpdateCb()"
        ></conditional-branch-editor>

        <!-- Panel Editor -->
        <panel-editor ref="panelEditor" v-on:updated="refreshNode()" :node="node" :case-obj="caseObj"></panel-editor>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseForm from "@/components/BaseForm"
import BaseModal from "@/components/BaseModal"
import HelpText from "@/components/HelpText"
import ConditionalBranchEditor from "@/components/ConditionalBranchEditor"
import NodeSelect from "@/components/NodeSelect"
import NodeContentPreview from "@/components/NodeContentPreview"
import PanelEditor from "@/components/PanelEditor"
import LibraryItemSelector from "@/components/LibraryItemSelector"
import NodeComments from "@/components/NodeComments"
import { continueRequirementOptions } from "@/assets/js/settings.js"

export default {
    name: "NodeDetail",
    components: {
        BaseForm,
        BaseModal,
        HelpText,
        ConditionalBranchEditor,
        NodeSelect,
        NodeContentPreview,
        PanelEditor,
        LibraryItemSelector,
        NodeComments,
    },
    data() {
        return {
            node: {
                columns: [],
                previous_nodes: [],
                next_nodes: [],
                conditional_branches: [],
            },
            newNode: {},
            savedNode: {
                end_node: false,
                final_node: false,
                columns: [],
            },
            modalLoading: false,
            cloneIntoLibraryItem: null,
            cloneNodeOutsideCase: false,
            newNodeFieldsConfig,
            continueButtonFields: [
                {
                    name: "Button Label",
                    key: "continue_button_text",
                    maxlength: 50,
                    placeholder: "Continue",
                    subtext: "Add a custom continue button label for this screen.",
                    showLimit: true,
                },
            ],
            continueRequirementOptions: continueRequirementOptions,
            comments: [],
        }
    },
    computed: {
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        editLocked() {
            if (!this.caseIsReadOnly) {
                return false
            }
            return this.caseIsReadOnly
        },
        cases() {
            return this.$store.getters.caseList
        },
        loading() {
            return this.$store.getters.loading
        },
        scrollIntoView() {
            return this.$route.query.scrollIntoView
        },
        caseID() {
            return this.$route.params.id
        },
        caseObj() {
            return this.$store.getters.case
        },
        caseBaseUrl() {
            return "/library/" + this.$route.params.id + "/"
        },
        selectedNodeNumber() {
            return this.$store.getters.selectedNode.number
        },
        nodeID() {
            return this.$route.params.nodeID
        },
        nodeOptions() {
            let allNodes = [...this.$store.getters.nodeOptions]
            // Remove the node that's currently being viewed.
            let index = allNodes.findIndex((n) => n.id === this.node.id)
            allNodes.splice(index, 1)
            return allNodes
        },
        continueNodeID() {
            return this.node.continue_node
        },
        nodeFormFieldsConfig() {
            let vm = this
            return [
                {
                    name: "Completion Point",
                    key: "completion_node",
                    type: "checkbox",
                    required: false,
                    description: "Check this box if reaching this screen means the learner has completed the learning experience.",
                    positionIndent: true,
                },
                {
                    name: "End Screen",
                    key: "final_node",
                    type: "checkbox",
                    required: false,
                    description:
                        "Check this box if this is a final screen in the learning experience.",
                    positionIndent: true,
                },
                {
                    name: "Fail Screen",
                    key: "end_node",
                    type: "checkbox",
                    required: false,
                    description:
                        "Check this box if visiting this screen means the student has failed the learning experience, and should start over.",
                    positionIndent: true,
                },
                {
                    name: "Enable Navigation",
                    description:
                        "Check this box to enable navigation on this screen (requires navigation to be enabled at the learning experience level).",
                    key: "navigation_enabled",
                    type: "checkbox",
                    required: false,
                    hidden() {
                        return !vm.caseObj.navigation_enabled
                    },
                    positionIndent: true,
                },
                {
                    name: "Show Continue Button",
                    key: "continue_button",
                    type: "checkbox",
                    required: false,
                    description:
                        "Check this box if the continue button should be shown on this screen. Optionally edit the label of the continue button.",
                    positionIndent: true,
                    editBtnText: "Label",
                },
            ]
        },
        userOrganizationID() {
            let org = this.$store.getters.currentOrganization
            if (org) {
                return org.id
            }
            return null
        },
        // A list of all the panels contained in the current node, as a single flat list.
        nodePanelsFlat() {
            return this.node.columns.reduce((acc, column) => acc.concat(column.panels), [])
        },
        nodeIsEmpty() {
            return this.node.columns.length === 0 && !this.loading
        },
        panelSelectableChoices() {
            return this.nodePanelsFlat
                .filter((p) => ["multiselect", "singleselect", "image_hotspots", "multichoice"].includes(p.panel_type))
                .map((p) => p.choices)
                .flat()
        },
        panelCategoriesChoices() {
            return this.nodePanelsFlat
                .filter((p) => ["categories"].includes(p.panel_type))
                .map((p) => p.choices)
                .flat()
        },
        panelFormChoices() {
            return this.nodePanelsFlat
                .filter((p) => ["custom_form"].includes(p.panel_type))
                .map((p) => p.choices)
                .flat()
        },
        cloneNodeCaseID() {
            return this.cloneIntoLibraryItem ? this.cloneIntoLibraryItem.case.id : null
        },
        cloneBtnDisabled() {
            return this.loading || (this.cloneNodeOutsideCase && !this.cloneNodeCaseID)
        },
        autoNumberingEnabled() {
            return this.caseObj.node_auto_numbering_enabled
        },
    },
    watch: {
        nodeID() {
            this.fetchNode()
            this.$refs.nodeEditForm.resetValidate()
        },
        selectedNodeNumber(value) {
            this.node.number = value
        },
    },
    methods: {
        // Fetch data about the node.
        fetchNode() {
            this.$store.dispatch("addLoad")
            let vm = this
            this.$store
                .dispatch("fetchNode", this.nodeID)
                .then(function (nodeObj) {
                    vm.node = nodeObj
                    vm.savedNode = Object.assign({}, nodeObj)
                    if (vm.scrollIntoView) {
                        vm.scrollToSelf()
                    }
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        // Refresh the current node being viewed in the datastore.
        refreshNode() {
            this.$store.dispatch("addLoad")
            let vm = this
            this.$store
                .dispatch("refreshNode", this.nodeID)
                .then(function (nodeObj) {
                    vm.node = nodeObj
                    vm.savedNode = Object.assign({}, nodeObj)
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        conditionalBranchUpdateCb() {
            this.$store.commit("mapRequiresUpdate")
            this.refreshNode()
        },
        // Scroll to the currently selected node.
        scrollToSelf() {
            let vm = this
            let nodeElement = document.getElementById("listnode" + vm.node.id)
            // Hack fix - if for whatever reason the node list menu isn't rendered yet,
            // this selector will be null. In that case, skip the scroll.
            if (!nodeElement) {
                return
            }
            vm.$nextTick(function () {
                nodeElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                })
            })
        },
        // Save the node.
        saveNode() {
            // Check form validity.
            if (!this.$refs.nodeEditForm.validate()) {
                return
            }
            // Check validity of the node selector separately.
            let hasConditionalBranches = this.node.conditional_branches.length
            if (this.node.continue_button && !hasConditionalBranches) {
                if (!this.$refs.continueNodeSelect.validate()) {
                    return
                }
            }

            // Hack to fix blank custom continue requirement.
            if (!this.node.correct_answers_required_num) {
                this.node.correct_answers_required_num = 0
            }

            let nodeFormatted = Object.assign({}, this.node)
            delete nodeFormatted.panels
            delete nodeFormatted.next_nodes
            delete nodeFormatted.previous_nodes

            this.$store.dispatch("addLoad")
            let vm = this
            http.put("/api/nodes/" + this.node.id + "/", nodeFormatted).then(function (response) {
                vm.$store.dispatch("removeLoad")
                vm.$notify({
                    group: "main",
                    type: "success",
                    text: "Screen updated.",
                    data: {},
                })
                vm.node = response.data

                // Set the selected node in the data store.
                vm.$store.commit("setSelectedNode", Object.assign({}, response.data))
                vm.$store.commit("mapRequiresUpdate")

                // Save a cloned version of the saved node, separate from the editable one.
                vm.savedNode = Object.assign({}, response.data)

                // Reset form validation.
                vm.$refs.nodeEditForm.resetValidate()
                vm.$refs.continueNodeSelect.reset()

                vm.$store.dispatch("refreshCase", vm.$route.params.id)
            })
        },
        // Open the delete node modal.
        openDeleteNode() {
            $("#delete-node-modal").modal("show")
        },
        // Delete the node.
        deleteNode() {
            this.modalLoading = true
            let vm = this
            http.put("/api/nodes/" + this.nodeID + "/remove/")
                .then(async function () {
                    vm.modalLoading = false
                    $("#delete-node-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Screen deleted.",
                        data: {},
                    })

                    let currentNodeIndex = vm.caseObj.nodes.findIndex((n) => n.id === Number(vm.nodeID))
                    let previousNode = vm.caseObj.nodes[currentNodeIndex - 1]

                    // Update case.
                    let caseID = vm.$route.params.id
                    await vm.$store.dispatch("refreshCase", caseID)
                    vm.$emit("update-node-order")
                    vm.$store.commit("mapRequiresUpdate")

                    if (previousNode) {
                        // Navigate to the previous node in the list.
                        vm.$router.push(`${vm.caseBaseUrl}nodes/${previousNode.id}/`)
                    } else {
                        // Navigate to the settings view.
                        vm.$router.push(vm.caseBaseUrl + "settings")
                    }
                })
                .catch(function () {
                    vm.modalLoading = false
                })
        },
        // Launch a case session to preview the current node.
        previewNode() {
            this.$store.dispatch("clearSession")
            this.$store.dispatch("addLoad")
            let vm = this
            let previewSessionStartURL = "/api/cases/" + vm.caseID + "/preview/start/"
            http.post(previewSessionStartURL, {
                startNodeID: this.node.id,
            }).then(function (response) {
                vm.$store.dispatch("removeLoad")
                vm.$router.push(`${vm.caseBaseUrl}preview/${response.data.session_id}/`)
            })
        },
        // Open the clone node modal.
        openCloneNode() {
            if (!this.cloneNodeOutsideCase) {
                this.cloneIntoLibraryItem = null
            }
            if (this.editLocked) {
                this.cloneNodeOutsideCase = true
            }
            // Fetch an initial set of library items to display as options to clone into.
            this.$refs.itemSelector.searchDebounced("")
            $("#clone-node-modal").modal("show")
        },
        // Create a copy of a node.
        cloneNode() {
            this.modalLoading = true
            let vm = this
            http.post("/api/nodes/" + this.nodeID + "/clone/", {
                targetCaseID: this.cloneNodeCaseID,
            })
                .then(async function (response) {
                    $("#clone-node-modal").modal("hide")

                    let notificationMessage = "Screen cloned."
                    if (response.data.case_id === Number(vm.caseID)) {
                        // Cloning into the current case, so navigate to the newly created node.
                        vm.$router.push(vm.caseBaseUrl + "nodes/" + response.data.id + "/")
                    } else {
                        notificationMessage = "Screen cloned into learning experience."
                    }

                    vm.$store.commit("mapRequiresUpdate")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: notificationMessage,
                        data: {},
                    })

                    await vm.$store.dispatch("refreshCase", vm.caseID)
                    if (vm.cloneNodeCaseID == vm.caseID) {
                        vm.$emit("update-node-order")
                        vm.$nextTick(() => vm.scrollToSelf())
                    }
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Create a new node.
        createNode() {
            // Check form validity.
            if (!this.$refs.createNodeForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            // Add an attribute to link the continue node (of the current node) to
            // the new node, once created.
            this.newNode.linkFromNodeID = this.nodeID
            http.post("/api/cases/" + this.$route.params.id + "/nodes/", this.newNode).then(function (response) {
                vm.$store
                    .dispatch("refreshCase", vm.$store.getters.case.id)
                    .then(function () {
                        vm.modalLoading = false
                        vm.node.continue_node = response.data.id
                        $("#create-node-modal").modal("hide")
                    })
                    .catch(function () {
                        vm.modalLoading = false
                        $("#create-node-modal").modal("hide")
                    })
            })
        },
        // Open the create node modal.
        openCreateNode() {
            this.$refs.createNodeForm.resetValidate()
            // Initialize case object for editing.
            this.newNode = {}
            $("#create-node-modal").modal("show")
        },
        // Open the conditional branching editor.
        openConditionalBranching() {
            this.$refs.conditionalBranchEditor.open()
        },
        // A callback ran when an edit button click event is received on the node form.
        nodeFormEditCb(field) {
            if (field == "continue_button") {
                $("#edit-continue-modal").modal("show")
            }
        },
        // Save the continue button label for this node.
        saveContinueButton() {
            $("#edit-continue-modal").modal("hide")
            this.saveNode()
        },
        openEditPanel(panel) {
            let panelCopy = Object.assign({}, panel)
            this.$refs.panelEditor.open(panelCopy)
        },
        openComments() {
            this.modalLoading = true
            let vm = this
            $("#comments-modal").modal("show")
            http.get(`/api/nodes/${this.nodeID}/comments/`)
            .then((response) => {
                vm.comments = response.data
            })
            .finally(() => {
                vm.modalLoading = false
            })
        },
    },
    beforeMount() {
        if (this.$store.getters.selectedNode.id != this.nodeID) {
            this.$store.dispatch("clearSelectedNode")
        }
    },
    created() {
        this.fetchNode()
    },
    // Called by the router before navigating away from this component.
    beforeRouteLeave(to, from, next) {
        this.$refs.panelEditor.closeEditModals()
        next()
    },
}

const newNodeFieldsConfig = [
    {
        name: "Title",
        key: "title",
        required: true,
        maxlength: 150,
    },
    {
        name: "Number",
        placeholder: "Number (optional)",
        key: "number",
        type: "number",
        required: false,
    },
]
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/nodemap.scss";
@import "@/assets/styles/paneledit.scss";

.settings-header {
    font-size: 1.15rem;
}

.number-input {
    max-width: 80px;
}

.node-preview-container {
    min-height: 130px;
}

.edit-link {
    top: 10px;
}

.subtitle {
    font-size: 13px;
}
</style>
